<script setup>
import { onMounted, onUnmounted, ref, watchEffect, computed } from "vue";
import { useQuasar } from "quasar";
import { useRoute, useRouter } from "vue-router";
import { fetchWithHeaders } from "@/utils";
import { toDjangoDate, toDate, toTime, toISOString, toDuration, dayjs, sessionFormat, shortenModule } from "@/filters";
import AdminRegistrationDialog from "@/components/AdminRegistrationDialog.vue";
import MatriculeImg from "@/components/MatriculeImg.vue";

const $q = useQuasar();
const router = useRouter();
const route = useRoute();
const DEBUG = window._env_.DEBUG === "true";
const session = ref();
const slots = ref([]);
const selectedIndex = ref(null);
const registrations = ref([]);
let selectedRegistration = null;
const now = ref(new Date());
let intervalId;
onMounted(() => {
    intervalId = setInterval(() => {
        now.value = new Date();
    }, 1000);
});
onUnmounted(() => {
    clearInterval(intervalId);
});

async function getSlotsData() {
    const urlSlots = `${window._env_.API_ENDPOINT}/slots/?session=${route.params.sessionCode}&start=${route.params.selectedDate}`;
    const result = await fetchWithHeaders(urlSlots);
    slots.value = result || [];
}

watchEffect(async () => {
    if (!route.params.sessionCode) return;
    const url = `${window._env_.API_ENDPOINT}/sessions/${route.params.sessionCode}/`;
    session.value = await fetchWithHeaders(url);
    if (!session.value) return; // Si la session n'a pas pu être chargée, arrêter
    if (!route.params.selectedDate) {
        // TODO nice to have compute most useful date
        goToDate(toDjangoDate(dayjs()));
    } else {
        if (!route.params.selectedIndex) {
            selectedIndex.value = null;
        } else {
            selectedIndex.value = parseInt(route.params.selectedIndex);
        }
        await getSlotsData();
        if (slots.value && slots.value.length > 0) {
            if (selectedIndex.value == null) {
                selectedIndex.value = 0;
            }
        }
    }
    if (route.params.registrationPk != null) {
        selectedRegistration = route.params.registrationPk;
    }
});

async function loadRegistrations() {
    const url = `${window._env_.API_ENDPOINT}/registrations/?slot=${slots.value[selectedIndex.value].pk}`;
    const result = await fetchWithHeaders(url);
    registrations.value = result || [];
}

watchEffect(() => {
    if (selectedIndex.value !== null && slots.value && slots.value.length > 0) {
        loadRegistrations();
    }
});
function goToDate(date, slotIndex = 0) {
    selectedIndex.value = slotIndex;
    router.push(`/admin/session/${route.params.sessionCode}/${date}/${slotIndex}/`);
}
function limitToDateRange(date) {
    return date >= session.value.open_date.replace(/-/g, "/") && date <= session.value.close_date.replace(/-/g, "/");
}
const debugCanCancel = ref(true);
const canCancel = computed(() => {
    if (DEBUG) return debugCanCancel.value;
    if (selectedIndex.value !== null && selectedIndex.value !== undefined) {
        if (slots.value.length == 0) {
            return false;
        }
        return now.value.getTime() < new Date(slots.value[selectedIndex.value].start).getTime() - 1000 * 3600;
    }
    return false;
});

const initialPagination = {
    page: 1,
    rowsPerPage: 0,
    sortBy: "seat",
    descending: false,
};
const columns = [
    { name: "seat", label: "Place", field: "seat", sortable: true, required: true },
    {
        name: "user",
        label: "Etudiant",
        field: row => row.user_data?.surname.normalize("NFD").replace(/\p{Diacritic}/gu, ""),
        align: "left",
        sortable: true,
        required: true,
    },
    {
        name: "module",
        label: "Module",
        align: "left",
        sortable: true,
        required: true,
        classes: "white-space-normal",
        style: "max-width: 200px",
        field: row => shortenModule(`${row.user_data?.plan} ${row.user_data?.etape}`),
    },
    {
        name: "epreuves",
        label: "Epreuves",
        align: "left",
        classes: "white-space-normal",
        style: "max-width: 500px",
        sortable: false,
        required: true,
        field: row => row.user_data?.epreuves,
    },
    {
        name: "comment",
        classes: "white-space-normal",
        label: "Commentaire",
        required: true,
        align: "left",
        field: "comment",
    },
    { name: "cancel", label: "Inscription", align: "center" },
    { name: "arrived_at", label: "Arrivé", field: "arrived_at", align: "left" },
    { name: "duration", label: "Durée", field: "arrived_at", align: "left" },
    { name: "left_at", label: "Départ", field: "left_at", align: "left" },
    { name: "remark", label: "", field: "admin_remark", required: true, align: "center" },
];

function createRegistration() {
    const registration = {
        slot: slots.value[selectedIndex.value].pk,
        user_email: "",
        user_data: {
            givenName: "",
            surname: "",
            phone: "",
            plan: "",
            etape: "",
            matricule: "",
            pernum: "",
            epreuves: [],
        },
        comment: "",
        admin_remark: "",
    };
    editRegistration(registration);
}

function editRegistration(registration) {
    $q.dialog({
        component: AdminRegistrationDialog,
        componentProps: {
            registration,
        },
    }).onOk(() => {
        loadRegistrations();
        getSlotsData();
    });
}

function cancelRegistration(registration) {
    $q.dialog({
        title: "Suppression?",
        message: `Supprimer l'inscription de ${registration.user_email}?`,
        cancel: true,
        persistent: true,
    }).onOk(async () => {
        const url = `${window._env_.API_ENDPOINT}/registrations/${registration.pk}`;
        await fetchWithHeaders(url, {
            method: "DELETE",
        });
        registrations.value.splice(registrations.value.indexOf(registration), 1);
        getSlotsData();
    });
}

async function patchRegistration(registration, patch) {
    adjustRegistrationTime(registration, patch);

    const url = `${window._env_.API_ENDPOINT}/registrations/${registration.pk}/`;
    await fetchWithHeaders(url, {
        method: "PATCH",
        body: JSON.stringify(patch),
    });
    Object.keys(patch).forEach(key => {
        registration[key] = patch[key];
    });
}

function adjustRegistrationTime(registration, patch) {
    const left_at = patch["left_at"];
    if (!left_at) {
        return;
    }
    const selectedSlots = slots.value.filter(slot => slot["pk"] == registration.slot);
    if (selectedSlots.length != 1) {
        return;
    }
    const selectedSlot = selectedSlots[0];
    if (new Date(left_at) > new Date(selectedSlot.end)) {
        patch["left_at"] = selectedSlot.end;
    }
}

function highlightSelectedRegistration(registration) {
    if (registration.pk == selectedRegistration) {
        return "highlighted";
    }
}

function handleRowClick(event, row) {
    editRegistration(row);
}
</script>
<template>
    <main v-if="session">
        <q-card class="q-pa-md q-mt-lg q-mx-auto">
            <div class="q-mb-sm row q-col-gutter-lg">
                <div class="text-h4">Session {{ sessionFormat(session.code) }}</div>
                <div class="text-grey-5 print-hide">
                    Inscriptions {{ session.registrations_count }} / {{ session.seats_total || 0 }} places dans
                    {{ session.slots_count }} créneaux<br />Places libres {{ session.seats_free || 0 }}
                </div>
                <q-toggle v-if="DEBUG" v-model="debugCanCancel" label="Debug canCancel" class="print-hide" />
                <q-space />
                <div>
                    <q-btn
                        label="ajouter réservation"
                        color="primary"
                        outline=""
                        icon="fa-solid fa-plus"
                        class="print-hide"
                        to="/consultation"
                    />
                </div>
            </div>
            <div class="row print-hide">
                <div>{{ toDate(session.open_date) }} - {{ toDate(session.close_date) }}</div>
            </div>
            <div class="row q-my-md">
                <div class="col">
                    <q-btn
                        label="Précédent"
                        outline
                        color="primary"
                        class="print-hide"
                        @click="goToDate(toDjangoDate(dayjs(route.params.selectedDate).subtract(1, 'day')))"
                    />
                </div>
                <div class="col text-center">
                    <q-btn outline color="primary" :label="toDate(route.params.selectedDate)">
                        <q-popup-proxy>
                            <q-date
                                :model-value="route.params.selectedDate"
                                mask="YYYY-MM-DD"
                                :options="limitToDateRange"
                                @update:model-value="goToDate($event)"
                            ></q-date>
                        </q-popup-proxy>
                    </q-btn>
                </div>
                <div class="col text-right">
                    <q-btn
                        label="Suivant"
                        outline
                        color="primary"
                        class="print-hide"
                        @click="goToDate(toDjangoDate(dayjs(route.params.selectedDate).add(1, 'day')))"
                    />
                </div>
            </div>
            <q-tabs
                v-model="selectedIndex"
                class="text-grey"
                active-color="primary"
                indicator-color="primary"
                align="left"
            >
                <q-tab
                    v-for="(slot, index) in slots"
                    :key="slot.pk"
                    :name="index"
                    :class="{ 'print-hide': index !== selectedIndex }"
                    :label="`${toTime(slot.start)} (
                ${slot.registrations_count} / ${slot.seats} )`"
                    @click="goToDate(toDjangoDate(dayjs(route.params.selectedDate)), index)"
                />
            </q-tabs>
            <q-separator />

            <q-table
                v-if="selectedIndex !== null"
                row-key="pk"
                :rows="registrations"
                :pagination="initialPagination"
                :columns="columns"
                hide-bottom
                square
                :visible-columns="canCancel ? ['cancel'] : ['cancel', 'arrived_at', 'duration', 'left_at']"
                @row-click="handleRowClick"
            >
                <template #body-cell-module="props">
                    <q-td :props="props" :class="highlightSelectedRegistration(props.row)">
                        <div>
                            {{ shortenModule(props.row.user_data?.plan) }} <br />
                            {{ props.row.user_data?.etape }}
                        </div>
                    </q-td>
                </template>
                <template #body-cell-epreuves="props">
                    <q-td :props="props" :class="highlightSelectedRegistration(props.row)">
                        <div v-if="props.value">
                            Total: {{ props.value.length }}
                            <ul class="q-my-none q-pl-md">
                                <li v-for="(e, index) in props.value" :key="index">
                                    {{ e.eprnom }}
                                </li>
                            </ul>
                        </div>
                    </q-td>
                </template>
                <template #body-cell-user="props">
                    <q-td :props="props" :class="highlightSelectedRegistration(props.row)" style="min-width: 250px">
                        <div class="row items-start no-wrap q-gutter-sm">
                            <matricule-img :matricule="props.row.user_data?.matricule" class="matricule-picture" />
                            <div class="col">
                                <div class="text-weight-medium">
                                    {{ props.row.user_data?.surname?.toUpperCase() }}
                                </div>
                                <div>
                                    {{ props.row.user_data?.givenName }}
                                </div>
                                <div class="text-italic text-grey-7">
                                    {{ props.row.user_data?.matricule }}
                                </div>
                                <q-btn
                                    icon="fa-regular fa-envelope"
                                    color="primary"
                                    round
                                    flat
                                    size="xs"
                                    class="q-mt-xs"
                                    :href="`mailto:${props.row.user_email}?subject=[HEC] Réservation`"
                                    target="_blank"
                                    @click.stop
                                >
                                    <q-tooltip>
                                        {{ props.row.user_email }}
                                    </q-tooltip>
                                </q-btn>
                            </div>
                        </div>
                    </q-td>
                </template>
                <template #body-cell-cancel="props">
                    <q-td :props="props" :class="highlightSelectedRegistration(props.row)">
                        <div>
                            <q-btn
                                label="Annuler"
                                flat
                                color="negative"
                                class="print-hide"
                                @click.stop="cancelRegistration(props.row)"
                            />
                        </div>
                    </q-td>
                </template>
                <template #body-cell-arrived_at="props">
                    <q-td :props="props" :class="highlightSelectedRegistration(props.row)">
                        <div>
                            <span v-if="props.value">{{ toTime(props.value) }}</span>
                            <span v-if="props.row.no_show" class="text-negative">NO SHOW</span>
                            <q-btn
                                v-if="!props.value && !props.row.no_show"
                                label="arrivé"
                                outline
                                color="primary"
                                class="print-hide"
                                @click.stop="patchRegistration(props.row, { arrived_at: toISOString() })"
                            />
                        </div>
                    </q-td>
                </template>
                <template #body-cell-duration="props">
                    <q-td :props="props" :class="highlightSelectedRegistration(props.row)">
                        <div v-if="props.value" class="text-weight-bold">
                            {{ toDuration(props.value, props.row.left_at || now, "HH:mm:ss") }}
                        </div>
                    </q-td>
                </template>
                <template #body-cell-left_at="props">
                    <q-td :props="props" :class="highlightSelectedRegistration(props.row)">
                        <div>
                            <span v-if="props.value">{{ toTime(props.value) }}</span>
                            <q-btn
                                v-if="props.row.arrived_at && !props.value"
                                label="départ"
                                unelevated
                                color="primary"
                                class="print-hide"
                                @click.stop="patchRegistration(props.row, { left_at: toISOString() })"
                            />
                            <q-btn
                                v-if="!props.row.arrived_at && !props.row.no_show"
                                label="no show"
                                outline
                                color="negative"
                                class="print-hide"
                                @click.stop="patchRegistration(props.row, { no_show: true })"
                            />
                        </div>
                    </q-td>
                </template>
                <template #body-cell-remark="props">
                    <q-td :props="props" :class="highlightSelectedRegistration(props.row)">
                        <div v-if="props.row.admin_remark">
                            <q-icon name="fa-solid fa-triangle-exclamation" style="color: #f0910a"> </q-icon>
                        </div>
                    </q-td>
                </template>
            </q-table>
            <div class="row q-mt-lg q-gutter-md print-hide">
                <q-btn
                    label="ajouter réservation dans ce slot"
                    color="primary"
                    outline=""
                    icon="fa-solid fa-plus"
                    class="print-hide"
                    :disable="selectedIndex === null"
                    @click="createRegistration"
                />
                <q-space />
                <q-btn color="primary" class="nav-button" label="Retour" :to="`/admin/`" />
            </div>
        </q-card>
    </main>
</template>
<style scoped>
@media print {
    .q-card {
        box-shadow: none;
        margin-top: 0 !important;
    }
}

.matricule-picture {
    flex-shrink: 0; /* Empêche la photo de rétrécir */
}

/* Améliorer l'espacement des cellules du tableau */
:deep(.q-table td) {
    padding: 12px 8px;
    vertical-align: top;
}

:deep(.q-table th) {
    padding: 12px 8px;
}

.highlighted {
    background-color: #003754;
    animation-name: bckanim;
    animation-fill-mode: forwards;
    animation-duration: 1s;
    animation-delay: 250ms;
}
@keyframes bckanim {
    0% {
        background-color: #003754;
    }
    100% {
        background-color: transparent;
    }
}
</style>
